import { getAllPages, getPageBySlug } from 'api/cms';
import { H4 } from 'components/headings';
import Layout from 'components/layout';
import RichText from 'components/rich-text';
import SEO from 'components/seo';
import ContentSection from 'components/ui/content-section';
import { Text } from 'components/ui/text';
import Cookies from 'js-cookie';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { useQuery } from 'react-query';
import { Page as PageData } from 'types/sanity';
import { backgroundImages, settings } from 'utils/config';

interface Props {
  page?: PageData;
  preview?: boolean;
  errorCode?: number;
}

const PreviewLayout: FC = ({ children }) => (
  <Layout>
    <ContentSection>{children}</ContentSection>
  </Layout>
);

const Page: NextPage<Props> = (props) => {
  const previewEnabled = Cookies.get(settings.previewCookieName) === 'enabled';

  const router = useRouter();
  const { slug = '' }: any = router.query;

  const { data, isLoading } = useQuery(
    [slug, previewEnabled],
    () => getPageBySlug(slug, previewEnabled),
    {
      initialData: !previewEnabled ? { page: props.page } : undefined,
      enabled: previewEnabled,
    }
  );

  if (previewEnabled && isLoading) {
    return (
      <PreviewLayout>
        <Text>Loading preview...</Text>
      </PreviewLayout>
    );
  }

  // Page not found
  if (props.errorCode) {
    return (
      <PreviewLayout>
        <Text>Error: {props.errorCode}</Text>
      </PreviewLayout>
    );
  }

  // Present page without data
  if (!data || !data.page) {
    return (
      <PreviewLayout>
        <Text>No page data</Text>
      </PreviewLayout>
    );
  }

  const { page } = data;

  return (
    <>
      <SEO title={page.title} description={page.description} />
      <Layout
        preview={previewEnabled}
        mainBackground={backgroundImages.treesBurn}
      >
        <ContentSection>
          <H4 as='h1'>{page.title}</H4>
          <RichText blocks={page.body} />
        </ContentSection>
      </Layout>
    </>
  );
};

export const getStaticProps: GetStaticProps<Props> = async ({
  params,
  preview = false,
}) => {
  const slug = params?.slug as string;
  const { page } = await getPageBySlug(slug, preview);

  // Page doesn't exist
  if (!page) {
    return {
      props: { errorCode: 404 },
    };
  }

  return {
    props: {
      page,
      preview,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { pages: allPages } = await getAllPages();
  const paths =
    allPages?.map((page: any) => ({
      params: {
        slug: page.slug.current,
      },
    })) || [];

  return {
    paths,
    fallback: false,
  };
};

export default Page;
